import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Dialog, FlatButton, MenuItem, SelectField, TextField } from 'material-ui';
import MapComponent from 'components/Regions/PortAdd/MapComponent';
import constants from 'constants';
import _ from 'lodash';

const TransformAreaToPortDialog = ({
    open,
    onClose,
    onSubmit,
    initialValues,
    countries,
}) => {
    const [warnings, setWarnings] = useState({});
    const [customErrors, setCustomErrors] = useState({});
    
    const initialCoordinates = initialValues && initialValues.coordinates 
        ? [
            typeof initialValues.coordinates[0] === 'string' 
                ? parseFloat(initialValues.coordinates[0]) || 0 
                : initialValues.coordinates[0] || 0,
            typeof initialValues.coordinates[1] === 'string' 
                ? parseFloat(initialValues.coordinates[1]) || 0 
                : initialValues.coordinates[1] || 0,
        ]
        : [0, 0];
    const defaultValues = {
        name: initialValues ? initialValues.name : '',
        country: initialValues ? initialValues.country : '',
        unLoCode: initialValues ? initialValues.unLoCode : '',
        coordinates: initialCoordinates,
        'coordinates.0': initialCoordinates[0],
        'coordinates.1': initialCoordinates[1],
    };
    const {
        control, 
        handleSubmit,
        errors,
        setValue,
        watch,
        trigger,
        reset,
    } = useForm({
        defaultValues,
        mode: 'all',
    });

    useEffect(() => {
        if (open) {
            reset(defaultValues);
        }
        return () => {
            reset(defaultValues);
        };
    }, [open]);

    const coordinates = watch('coordinates');
    const lat = watch('coordinates.0');
    const lng = watch('coordinates.1');
    const country = watch('country');
    const unLoCode = watch('unLoCode');
    
    useEffect(() => {
        if (country && unLoCode) {
            const countryObj = countries.find(c => c._id === country);
            if (countryObj && countryObj.shortNameCode) {
                if (!unLoCode.startsWith(countryObj.shortNameCode)) {
                    setWarnings(prev => ({
                        ...prev,
                        unLoCode: `UN LOCODE should start with country code: ${countryObj.shortNameCode}`,
                    }));
                } else {
                    setWarnings((prev) => {
                        const newWarnings = { ...prev };
                        delete newWarnings.unLoCode;
                        return newWarnings;
                    });
                }
            }
        } else {
            setWarnings((prev) => {
                const newWarnings = { ...prev };
                delete newWarnings.unLoCode;
                return newWarnings;
            });
        }
    }, [country, unLoCode, countries]);
    
    useEffect(() => {
        if (unLoCode && (unLoCode.length > 5 || unLoCode.length < 5)) {
            setCustomErrors(prev => ({
                ...prev,
                unLoCode: 'UN LOCODE must be 5 characters long',
            }));
        } else {
            setCustomErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.unLoCode;
                return newErrors;
            });
        }
    }, [unLoCode]);

    const onFormSubmit = (formData) => {
        if (Object.keys(customErrors).length > 0) {
            return;
        }
        
        const formattedData = {
            ...formData,
            coordinates: [
                parseFloat(_.get(formData, 'coordinates.0', 0)) || 0,
                parseFloat(_.get(formData, 'coordinates.1', 0)) || 0,
            ],
        };

        onSubmit(formattedData);
    };

    const handleMapCoordinateChange = (key, value) => {
        if (key === 'coordinates') {
            const numericCoords = [
                parseFloat(value[0]) || 0,
                parseFloat(value[1]) || 0,
            ];
            
            setValue('coordinates', numericCoords);
            setValue('coordinates.0', numericCoords[0]);
            setValue('coordinates.1', numericCoords[1]);
        }
    };

    return (
        <Dialog
            title='Transform area to port.'
            actions={[
                <FlatButton
                    label='OK'
                    primary
                    onClick={handleSubmit(onFormSubmit)}
                />,
                <FlatButton 
                    label='Cancel'
                    primary
                    onClick={onClose}
                />,
            ]}
            open={open}
            autoScrollBodyContent
            modal
        >
            <form>
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'Port name is required' }}
                    defaultValue={initialValues ? initialValues.name : ''}
                    render={({
                        onChange, onBlur, value, name, ref, 
                    }) => (
                        <TextField 
                            value={value}
                            defaultValue={value}
                            onChange={onChange}
                            onBlur={() => {
                                onBlur();
                                trigger('name');
                            }}
                            name={name}
                            inputRef={ref}
                            floatingLabelText='Port name'
                            errorText={errors.name ? errors.name.message : null}
                            fullWidth
                        />
                    )}
                />
                
                <Controller
                    name="country"
                    control={control}
                    rules={{ required: 'Country is required' }}
                    defaultValue={initialValues ? initialValues.country : ''}
                    render={({
                        onChange, onBlur, value, name, ref, 
                    }) => (
                        <SelectField
                            value={value}
                            onChange={(_e, _i, selectedValue) => onChange(selectedValue)}
                            onBlur={() => {
                                onBlur();
                                trigger('country');
                            }}
                            name={name}
                            ref={ref}
                            floatingLabelText='Country'
                            maxHeight={200}
                            errorText={errors.country ? errors.country.message : null}
                            fullWidth
                        >
                            {countries.map((c, i) => (
                                <MenuItem 
                                    key={`country_${i}`}
                                    value={c._id}
                                    primaryText={c.name}
                                />
                            ))}
                        </SelectField>
                    )}
                />
                
                <Controller
                    name="unLoCode"
                    control={control}
                    defaultValue={initialValues ? initialValues.unLoCode : ''}
                    render={({
                        onChange, onBlur, value, name, ref, 
                    }) => (
                        <div>
                            <TextField 
                                value={value}
                                onChange={(e) => {
                                    const input = e.target;
                                    const start = input.selectionStart;
                                    const end = input.selectionEnd;
                                    const upperValue = e.target.value.toUpperCase();
                                
                                    onChange(upperValue);
                                
                                    setTimeout(() => {
                                        input.setSelectionRange(start, end);
                                    }, 0);
                                }}
                                onBlur={onBlur}
                                name={name}
                                inputRef={ref}
                                floatingLabelText='UN LOCODE'
                                fullWidth
                                errorText={customErrors.unLoCode || null}
                            />
                            {!customErrors.unLoCode && warnings.unLoCode && (
                                <div style={{
                                    color: 'orange', fontSize: '12px', marginTop: '4px', marginBottom: '8px', 
                                }}
                                >
                                    {warnings.unLoCode}
                                </div>
                            )}
                        </div>
                    )}
                />
                
                <Controller
                    name="coordinates.0"
                    control={control}
                    rules={{ 
                        required: 'Latitude is required',
                        validate: value => !isNaN(parseFloat(value)) || 'Must be a number', 
                    }}
                    defaultValue={initialCoordinates[0]}
                    render={({
                        onChange, onBlur, value, name, ref, 
                    }) => (
                        <TextField 
                            value={value}
                            onChange={(e) => {
                                const numValue = parseFloat(e.target.value) || 0;
                                onChange(numValue);
                                setValue('coordinates', [numValue, parseFloat(lng) || 0]);
                            }}
                            onBlur={onBlur}
                            name={name}
                            inputRef={ref}
                            floatingLabelText='Latitude'
                            fullWidth
                            errorText={errors['coordinates.0'] ? errors['coordinates.0'].message : null}
                        />
                    )}
                />
                
                <Controller
                    name="coordinates.1"
                    control={control}
                    rules={{ 
                        required: 'Longitude is required',
                        validate: value => !isNaN(parseFloat(value)) || 'Must be a number', 
                    }}
                    defaultValue={initialCoordinates[1]}
                    render={({
                        onChange, onBlur, value, name, ref, 
                    }) => (
                        <TextField 
                            value={value}
                            onChange={(e) => {
                                const numValue = parseFloat(e.target.value) || 0;
                                onChange(numValue);
                                setValue('coordinates', [parseFloat(lat) || 0, numValue]);
                            }}
                            onBlur={onBlur}
                            name={name}
                            inputRef={ref}
                            floatingLabelText='Longitude'
                            fullWidth
                            errorText={errors['coordinates.1'] ? errors['coordinates.1'].message : null}
                        />
                    )}
                />
                
                <MapComponent
                    coordinates={[
                        parseFloat(lat) || 0,
                        parseFloat(lng) || 0,
                    ]}
                    change={handleMapCoordinateChange}
                    googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${constants.GOOGLE_MAP_API_KEY}`}
                    loadingElement={<div style={{ height: '100%' }} />}
                    containerElement={<div style={{ width: '100%', height: '300px', marginTop: '20px' }} />}
                    mapElement={<div style={{ height: '100%' }} />}
                />
            </form>
        </Dialog>
    );
};

export default TransformAreaToPortDialog;



// WEBPACK FOOTER //
// ./app/components/Regions/Dashboard/AreaToPortDialog.jsx